// api/campaigns.js - Campaign Management API

import { apiClient } from './client';

class CampaignAPI {
  constructor(client) {
    this.client = client;
  }

  // ========== BASIC CRUD ==========

  async getCampaigns(userId) {
    if (userId) {
      return this.client.request(`/api/campaigns?user_id=${userId}`);
    }
    return this.client.request('/api/campaigns');
  }

  async getCampaign(campaignId) {
    return this.client.request(`/api/campaigns/${campaignId}`);
  }

  async createCampaign(userId, name, options = {}) {
    const campaignData = {
      user_id: userId,
      name,
      difficulty: options.difficulty || 'normal',
      theme: options.theme || 'vanilla',
      day: options.day || 1,
      threat_level: options.threat_level || 0,
      supplies: options.supplies || 100,
      funds: options.funds || 0,
      intel: options.intel || 0,
      reputation: options.reputation || 0,
    };

    const result = await this.client.request('/api/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });

    // If backend returns {success: true, id: X}, fetch the full campaign
    if (result.success && result.id) {
      return this.getCampaign(result.id);
    }

    // If backend returns full campaign object, return it
    if (result.id) {
      return result;
    }

    // Fallback: construct campaign object from request data
    return {
      id: result.id,
      ...campaignData,
      created_at: new Date().toISOString(),
      last_played: new Date().toISOString(),
      total_playtime: 0,
      is_active: 1,
      game_over: 0,
      victory: 0,
    };
  }

  async updateCampaign(campaignId, updates) {
    return this.client.request(`/api/campaigns/${campaignId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteCampaign(campaignId) {
    return this.client.request(`/api/campaigns/${campaignId}`, {
      method: 'DELETE',
    });
  }

  // ========== GAME STATE ==========

  async saveCampaignProgress(campaignId, gameState) {
    return this.updateCampaign(campaignId, {
      day: gameState.day,
      threat_level: gameState.threatLevel,
      supplies: gameState.supplies,
      funds: gameState.funds,
      intel: gameState.intel,
      reputation: gameState.reputation,
      total_playtime: gameState.playtime,
      is_active: gameState.isActive ? 1 : 0,
      game_over: gameState.gameOver ? 1 : 0,
      victory: gameState.victory ? 1 : 0,
    });
  }

  async updateCampaignDay(campaignId, day) {
    return this.updateCampaign(campaignId, { day });
  }

  // ========== QUERIES ==========

  async getUserCampaigns(userId) {
    const campaigns = await this.getCampaigns();
    return campaigns.filter(c => c.user_id === userId);
  }

  async getActiveCampaign(userId) {
    const campaigns = await this.getUserCampaigns(userId);
    return campaigns.find(c => c.is_active === 1);
  }
}

// Export singleton instance
export const campaignAPI = new CampaignAPI(apiClient);

// Also export class
export default CampaignAPI;